/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 26-05-2026
 *
 * Uitleg-tooltip die verschijnt bij mouse-over op een node.
 * Toont vertaalde uitlegtekst op basis van nodetype en modelleerniveau.
 * Ondersteunt uitklapbare "lees meer"-inhoud en actie-events voor logging.
 *
 * De view-state (uitleg-tekst, fade-in, uitgeklapt) staat in signals zodat
 * mutaties vanuit een setTimeout of pointer-handler onder OnPush/zoneless
 * automatisch change detection triggeren.
 */

import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { zoekIngredientUitleg } from '../../../lib/ingredient-uitleg';

@Component({
    selector: 'dl-node-dom-uitleg',
    standalone: true,
    imports: [],
    templateUrl: './dl-node-dom-uitleg.component.html',
    styleUrls: ['./dl-node-dom-uitleg.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlNodeDomUitlegComponent implements OnChanges {

    /** Het nodetype waarvoor de uitleg getoond wordt */
    @Input() nodetype: string = '';

    /** Het effectieve modelleerniveau */
    @Input() modelleerniveau: number = 0;

    /**
     * Of de uitleg getoond mag worden. Wordt door de hover-groep aangestuurd;
     * de tooltip verschijnt alleen als er ook daadwerkelijk uitlegtekst is.
     * Positionering gebeurt door de groep (zie `dl-modelnode-hovergroep`).
     */
    @Input() zichtbaar: boolean = false;

    /** Actie-event voor logging (uitleg_toon, uitleg_hide, uitleg_muisin, uitleg_muisuit, uitleg_meer) */
    @Output() uitlegActie = new EventEmitter<string>();

    /** De korte uitleg-HTML (of leeg als niet gevonden) */
    readonly uitleg = signal('');

    /** De volledige uitleg-HTML inclusief "lees meer"-inhoud (of leeg als niet beschikbaar) */
    readonly uitlegVol = signal('');

    /** Of alle content zichtbaar is (na pointerenter) */
    readonly alleszichtbaar = signal(false);

    /** Of de volledige inhoud getoond wordt (na klik) */
    readonly toonVol = signal(false);

    /** Of de uitleg zojuist zichtbaar is geworden (voor fade-in animatie) */
    readonly wordtZichtbaar = signal(false);

    private readonly translateService = inject(TranslateService);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['nodetype'] || changes['modelleerniveau']) {
            this.updateUitleg();
        }
        if (changes['zichtbaar']) {
            if (this.zichtbaar) {
                // fade-in inzetten en tonen loggen
                this.wordtZichtbaar.set(false);
                setTimeout(() => {
                    this.wordtZichtbaar.set(true);
                    this.uitlegActie.emit('uitleg_toon');
                }, 100);
            } else {
                // verbergen: reset fade en log
                this.wordtZichtbaar.set(false);
                this.alleszichtbaar.set(false);
                this.toonVol.set(false);
                this.uitlegActie.emit('uitleg_hide');
            }
        }
    }

    /**
     * Zoekt de vertaalde uitlegtekst op voor het huidige nodetype en modelleerniveau.
     */
    private updateUitleg(): void {
        const gevonden = zoekIngredientUitleg(this.translateService, this.nodetype, this.modelleerniveau);
        this.uitleg.set(gevonden.kort);
        this.uitlegVol.set(gevonden.vol);
    }

    /** Zet alleszichtbaar op true bij pointerenter */
    opPointerEnter(): void {
        this.alleszichtbaar.set(true);
        this.uitlegActie.emit('uitleg_muisin');
    }

    /** Reset zichtbaarheid bij pointerleave */
    opPointerLeave(): void {
        this.alleszichtbaar.set(false);
        this.toonVol.set(false);
        this.uitlegActie.emit('uitleg_muisuit');
    }

    /** Toont de volledige inhoud bij klik (als uitlegVol beschikbaar is) */
    opKlik(): void {
        if (this.uitlegVol()) {
            this.toonVol.set(true);
            this.uitlegActie.emit('uitleg_meer');
        }
    }
}
